import {
  automaticThoughtIdType,
  ConceptKeyType,
  ConceptKeyType_automaticThought,
  ConceptKeyType_copingStrategy,
  ConceptKeyType_coreBelief,
  CoreBeliefKeyType,
  IntermediateBeliefKeyType,
  ReferenceKeyType,
} from "api/conceptApi";
import {
  Instruction,
  InstructionWithContent,
  InstructionWithTextarea,
} from "../BlockComponent";
import {
  CoachTextarea,
  ConceptNote,
  FeedbackLine,
  LineWithSolidLabel,
  SolidLabelLine,
  TypographyFromConceptNote,
  TypographyFromConceptNoteLine,
  TypographyFromDashboardLine,
  TypographyFromDataLine,
  TypographyLine,
} from "../CellComponent";

export const LastCardWithTextarea = [
  InstructionWithTextarea("common:lastCard1", { optional: true, id: "소감" }),
  Instruction("common:lastCard2"),
];

export const LastCard = [
  // InstructionWithTextarea("common:lastCard1", { optional: true, id: "소감" }),
  InstructionWithContent("common:lastCard1", [FeedbackLine()]),
  Instruction("common:lastCard2"),
];

export const RawAutomaticThoughtTemplate = (id: automaticThoughtIdType) => {
  const key = `automaticThought${id}` as ConceptKeyType_automaticThought;

  return [
    ...LineWithSolidLabel("상황", [
      TypographyFromConceptNote(key, "situation", { id: "상황" }),
    ]),
    ...LineWithSolidLabel("자동적 사고", [
      TypographyFromConceptNote(key, "thought", { id: "생각" }),
    ]),
    ...LineWithSolidLabel("감정/신체", [
      TypographyFromConceptNote(key, "emotionalPhysicalResponse", {
        id: "감정/신체",
      }),
    ]),
    ...LineWithSolidLabel("행동", [
      TypographyFromConceptNote(key, "behavior", { id: "행동" }),
    ]),
  ];
};

export const AutomaticThoughtTemplate = (id: string) => {
  const key = `automaticThought${id}` as ConceptKeyType_automaticThought;
  return [
    SolidLabelLine("상황"),
    TypographyFromDashboardLine(key, "situation", { id: "상황" }),

    SolidLabelLine("자동적 사고"),
    TypographyFromDashboardLine(key, "thought", {
      id: "생각",
      level: "title-md",
    }),
    SolidLabelLine("감정/신체"),
    TypographyFromDashboardLine(key, "emotionalPhysicalResponse", {
      id: "감정",
    }),
    SolidLabelLine("행동"),
    TypographyFromDashboardLine(key, "behavior", { id: "행동" }),
  ];
};

export const CopingStrategyTemplate = ({ isRaw }: { isRaw?: boolean } = {}) => {
  const key = "copingStrategy" as ConceptKeyType;
  return [
    SolidLabelLine("굴복"),
    isRaw
      ? TypographyFromConceptNoteLine(key, "surrender", { id: "굴복" })
      : TypographyFromDashboardLine(key, "surrender", { id: "굴복" }),
    SolidLabelLine("과잉보상"),
    isRaw
      ? TypographyFromConceptNoteLine(key, "overcompensation", {
          id: "과잉보상",
        })
      : TypographyFromDashboardLine(key, "overcompensation", {
          id: "과잉보상",
        }),
    SolidLabelLine("회피"),
    isRaw
      ? TypographyFromConceptNoteLine(key, "avoidance", { id: "회피" })
      : TypographyFromDashboardLine(key, "avoidance", { id: "회피" }),
  ];
};

export const IntermediateBeliefTemplate = ({
  isRaw,
  intermediateBeliefKeyList,
}: {
  isRaw?: boolean;
  intermediateBeliefKeyList?: IntermediateBeliefKeyType[];
} = {}) => {
  const key = "intermediateBelief" as ConceptKeyType;

  return [
    ...(intermediateBeliefKeyList?.includes("rules")
      ? [
          SolidLabelLine("규칙", { id: "rules_label" }),
          isRaw
            ? TypographyFromConceptNoteLine(key, "rules", { id: "규칙" })
            : TypographyFromDashboardLine(key, "rules"),
        ]
      : []),
    ...(intermediateBeliefKeyList?.includes("attitudes")
      ? [
          SolidLabelLine("태도", { id: "attitudes_label" }),
          isRaw
            ? TypographyFromConceptNoteLine(key, "attitudes", { id: "태도" })
            : TypographyFromDashboardLine(key, "attitudes"),
        ]
      : []),
    ...(intermediateBeliefKeyList?.includes("assumptions")
      ? [
          SolidLabelLine("가정", { id: "assumptions_label" }),
          isRaw
            ? TypographyFromConceptNoteLine(key, "assumptions", { id: "가정" })
            : TypographyFromDashboardLine(key, "assumptions"),
        ]
      : []),
  ];
};

export const CoreBeliefTemplate = ({
  isRaw,
  coreBeliefKeyList,
}: { isRaw?: boolean; coreBeliefKeyList?: CoreBeliefKeyType[] } = {}) => {
  const key = "coreBelief" as ConceptKeyType;

  return [
    ...(coreBeliefKeyList?.includes("coreBelief1")
      ? [
          SolidLabelLine("스스로에 대해"),
          isRaw
            ? TypographyFromConceptNoteLine(key, "coreBelief1", {
                id: "핵심 믿음 1",
              })
            : TypographyFromDashboardLine(key, "coreBelief1"),
        ]
      : []),
    ...(coreBeliefKeyList?.includes("coreBelief2")
      ? [
          SolidLabelLine("다른 사람에 대해"),
          isRaw
            ? TypographyFromConceptNoteLine(key, "coreBelief2", {
                id: "핵심 믿음 2",
              })
            : TypographyFromDashboardLine(key, "coreBelief2"),
        ]
      : []),
    ...(coreBeliefKeyList?.includes("coreBelief3")
      ? [
          SolidLabelLine("이 세상에 대해"),
          isRaw
            ? TypographyFromConceptNoteLine(key, "coreBelief3", {
                id: "핵심 믿음 3",
              })
            : TypographyFromDashboardLine(key, "coreBelief3"),
        ]
      : []),
  ];
};

export const CoreExperienceTemplate = ({ isRaw }: { isRaw?: boolean } = {}) => {
  const key = "coreExperience" as ConceptKeyType;
  return [
    SolidLabelLine("핵심 경험"),
    isRaw
      ? TypographyFromConceptNoteLine(key, undefined, { id: "핵심 경험" })
      : TypographyFromDashboardLine(key, undefined),
  ];
};

export const ConceptNoteTemplate = ({
  conceptKey,
}: {
  conceptKey: ConceptKeyType;
}) => {
  return [ConceptNote(conceptKey)];
};

export const MyWorryLines = () => {
  return LineWithSolidLabel(
    "나의 고민",
    // TypographyFromDataLine({ A: { id: "고민", taskId: "1-1-A" } }, "common:$A$")
    TypographyFromDataLine({ A: { id: "고민", taskId: "1-2-A" } }, "common:$A$")
  );
};

export const MyAutomaticThoughtLines = () => {
  return LineWithSolidLabel(
    "자동적 사고",
    TypographyFromDataLine({ A: { id: "생각" } }, "common:$A$")
  );
};
